import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../services/subscriptionService';
import { SubscriptionTier } from '../types';
import { getAuth } from 'firebase/auth';
import app from '../firebase';
import {
  trackSubscriptionModalViewed
} from '../services/analyticsService';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  trialDaysRemaining?: number;
  subscriptionStatus?: string;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  currentTier,
  trialDaysRemaining = 0,
  subscriptionStatus = 'trial'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSupportMessage, setShowSupportMessage] = useState(false);
  const [supportTier, setSupportTier] = useState<'e_rank' | 's_rank' | null>(null);

  // 📊 ANALYTICS: Track when modal is viewed
  useEffect(() => {
    if (isOpen) {
      trackSubscriptionModalViewed(currentTier, 'dashboard');
    }
  }, [isOpen, currentTier]);

  if (!isOpen) return null;

  const auth = getAuth(app);

  const handleSubscribe = async (tier: 'e_rank' | 's_rank') => {
    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be signed in to subscribe');
      }

      // Show support contact message instead of payment flow
      setSupportTier(tier);
      setShowSupportMessage(true);

    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to initiate subscription request');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be signed in');
      }

      // Show support contact message instead of customer portal
      setSupportTier(null); // No specific tier for management
      setShowSupportMessage(true);

    } catch (err: any) {
      console.error('Portal error:', err);
      setError(err.message || 'Failed to process subscription request');
    } finally {
      setLoading(false);
    }
  };

  const isTrialActive = currentTier === 'trial';
  // Only show "Manage Subscription" for paid subscriptions, NOT trials
  const hasActiveSubscription = (currentTier === 'e_rank' || currentTier === 's_rank') && subscriptionStatus === 'active';

  return (
    <div 
      className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-50 p-4 modal-backdrop animate-fade-in"
      onClick={(e) => {
        // Prevent closing when clicked outside if subscription is expired
        if (currentTier !== 'expired' && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl shadow-purple-500/20 max-w-md sm:max-w-2xl lg:max-w-4xl w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto animate-scale-in"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20 p-6 sm:p-8 flex items-center justify-between z-10"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <span className="text-2xl">💎</span>
              Subscription System
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {isTrialActive && trialDaysRemaining > 0
                ? `⏰ Trial Period: ${trialDaysRemaining} days remaining`
                : 'Choose your Hunter rank and unlock exclusive features'
              }
            </p>
          </div>
          {currentTier !== 'expired' && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <span className="text-xl text-gray-400">✕</span>
            </button>
          )}
        </div>

        <div className="p-6 sm:p-8 space-y-6 sm:space-y-8">
        {/* Subscription Expired Warning */}
        {currentTier === 'expired' && (
          <div 
            className="mb-6 sm:mb-8 p-6 sm:p-8 bg-gradient-to-b from-red-950/40 via-slate-950/30 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-3xl relative overflow-hidden"
            style={{
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            }}
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-red-500 text-xl sm:text-2xl flex-shrink-0">⚠️</span>
              <div>
                <p className="font-orbitron font-bold text-red-400 text-xs sm:text-sm mb-1">SUBSCRIPTION EXPIRED</p>
                <p className="text-red-200 text-xs font-rajdhani leading-relaxed">Your subscription has ended. Subscribe now to continue using Solo Leveling and keep your progress!</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div 
            className="mb-6 sm:mb-8 p-6 sm:p-8 bg-gradient-to-b from-red-950/40 via-slate-950/30 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-3xl relative overflow-hidden"
            style={{
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            }}
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-red-500 text-base sm:text-lg flex-shrink-0">⚠</span>
              <div>
                <p className="font-orbitron font-bold text-red-400 text-xs sm:text-sm mb-1">SYSTEM ERROR</p>
                <p className="text-red-200 text-xs font-rajdhani leading-relaxed">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Already Subscribed */}
        {hasActiveSubscription && (
          <div 
            className="mb-6 sm:mb-8 p-6 sm:p-8 bg-gradient-to-b from-blue-950/40 via-purple-950/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl relative overflow-hidden"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            }}
          >
            <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-system-blue/10 rounded-full blur-3xl"></div>
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 mb-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-green-400 text-base sm:text-lg">✓</span>
                    <p className="text-sm sm:text-lg font-orbitron font-bold text-white">
                      {subscriptionService.getTierDisplayName(currentTier)}
                    </p>
                  </div>
                  <span className="px-2 py-0.5 bg-green-500/20 border border-green-500/50 rounded text-green-400 text-[10px] font-bold uppercase self-start sm:self-auto">
                    Active
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-rajdhani leading-relaxed">
                  Access Stripe Customer Portal to manage billing, invoices, and cancel subscription
                </p>
              </div>
              <button
                onClick={handleManageSubscription}
                disabled={loading}
                className="bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 text-white px-4 sm:px-5 py-3 sm:py-3.5 text-xs sm:text-sm w-full sm:w-auto whitespace-nowrap touch-manipulation rounded-2xl hover:scale-105 transition-all relative"
                style={{
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                }}
              >
                {loading ? '⚙️ LOADING...' : '⚙️ MANAGE SUBSCRIPTION'}
              </button>
            </div>
          </div>
        )}

        {/* Subscription Tiers */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
          {/* E-Rank Tier */}
          <div 
            className="relative bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-system-blue/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-3xl overflow-hidden group hover:border-system-blue/70 transition-all hover:shadow-[0_0_30px_rgba(59,130,246,0.3)]"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            }}
          >
            {/* Discount Badge */}
            <div className="absolute top-2 right-2 z-10">
              <div className="px-2 py-0.5 bg-orange-500/90 rounded text-[9px] font-black font-orbitron tracking-wider text-white shadow-lg">
                50% OFF
              </div>
            </div>

            {/* Corner decoration - smaller */}
            <div className="absolute top-0 left-0 w-12 h-12 border-l-2 border-t-2 border-system-blue/30"></div>
            <div className="absolute bottom-0 right-0 w-12 h-12 border-r-2 border-b-2 border-system-blue/30"></div>

            <div className="p-6 sm:p-8 relative">
              {/* Rank Header */}
              <div className="mb-4 sm:mb-6 pb-4 border-b-2 border-b-system-blue/30">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                  <div className="w-1 h-1 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
                  <h3 className="text-sm sm:text-lg font-black font-orbitron tracking-wider text-system-blue uppercase">
                    E-RANK HUNTER
                  </h3>
                </div>
                <p className="text-[9px] sm:text-[10px] text-gray-400 font-rajdhani ml-2.5 sm:ml-3">Productivity Essentials Package</p>
              </div>

              {/* Price */}
              <div className="mb-4 sm:mb-6">
                <div className="flex items-baseline gap-1.5 sm:gap-2 mb-0.5">
                  <span className="text-2xl sm:text-3xl font-black font-orbitron text-white glow-text">$2.99</span>
                  <span className="text-xs sm:text-sm text-gray-400 line-through">$5.99</span>
                </div>
                <p className="text-[9px] sm:text-[10px] text-gray-400 font-rajdhani">per month • billed monthly</p>
              </div>

              {/* Features */}
              <div className="space-y-2 sm:space-y-2.5 mb-6 sm:mb-8">
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-blue text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-white">50</strong> AI Quest Generations/month</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-blue text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-white">5</strong> AI Program Generations/month</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-blue text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani">Up to <strong className="text-white">10</strong> Active Passive Skills</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-blue text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-white">6 Months</strong> History Retention</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-blue text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-white">3</strong> Premium Themes</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-blue text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani">Priority Email Support</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handleSubscribe('e_rank')}
                disabled={loading || currentTier === 'e_rank'}
                className="w-full bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 text-white py-3 sm:py-4 text-[10px] sm:text-xs font-bold tracking-wider disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group touch-manipulation rounded-2xl hover:scale-105 transition-all"
                style={{
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                <span className="relative z-10">
                  {currentTier === 'e_rank' ? '✓ CURRENT PLAN' : loading ? '⚙️ PROCESSING...' : '⚔️ UPGRADE TO E-RANK'}
                </span>
              </button>
            </div>
          </div>

          {/* S-Rank Tier */}
          <div 
            className="relative bg-gradient-to-b from-purple-900/40 via-slate-900/40 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-system-purple/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 rounded-3xl overflow-hidden group hover:border-system-purple/70 hover:shadow-[0_0_40px_rgba(168,85,247,0.4)] transition-all"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            }}
          >
            {/* Discount Badge */}
            <div className="absolute top-2 right-2 z-10">
              <div className="px-2 py-0.5 bg-orange-500/90 rounded text-[9px] font-black font-orbitron tracking-wider text-white shadow-lg">
                50% OFF
              </div>
            </div>

            {/* Recommended Badge */}
            <div className="absolute -top-0 left-1/2 transform -translate-x-1/2 z-10">
              <div className="px-3 py-1 bg-gradient-to-r from-system-blue to-system-purple rounded-b text-[9px] font-black font-orbitron tracking-widest text-white shadow-neon-purple flex items-center gap-1">
                <span className="text-yellow-300 text-xs">⭐</span>
                RECOMMENDED
              </div>
            </div>

            {/* Corner decoration - smaller */}
            <div className="absolute top-0 left-0 w-12 h-12 border-l-2 border-t-2 border-system-purple/50"></div>
            <div className="absolute bottom-0 right-0 w-12 h-12 border-r-2 border-b-2 border-system-purple/50"></div>

            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-system-purple/10 via-transparent to-system-blue/10 opacity-50"></div>

            <div className="p-6 sm:p-8 relative mt-5 sm:mt-6">
              {/* Rank Header */}
              <div className="mb-4 sm:mb-6 pb-4 border-b-2 border-b-system-purple/30">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                  <div className="w-1 h-1 bg-system-purple rounded-full animate-pulse shadow-neon-purple"></div>
                  <h3 className="text-sm sm:text-lg font-black font-orbitron tracking-wider text-system-purple uppercase">
                    S-RANK HUNTER
                  </h3>
                </div>
                <p className="text-[9px] sm:text-[10px] text-gray-400 font-rajdhani ml-2.5 sm:ml-3">Shadow Monarch Full Power</p>
              </div>

              {/* Price */}
              <div className="mb-4 sm:mb-6">
                <div className="flex items-baseline gap-1.5 sm:gap-2 mb-0.5">
                  <span className="text-2xl sm:text-3xl font-black font-orbitron text-white glow-text-purple">$4.99</span>
                  <span className="text-xs sm:text-sm text-gray-400 line-through">$9.99</span>
                </div>
                <p className="text-[9px] sm:text-[10px] text-gray-400 font-rajdhani">per month • billed monthly</p>
              </div>

              {/* Features */}
              <div className="space-y-2 sm:space-y-2.5 mb-6 sm:mb-8">
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-system-purple">UNLIMITED</strong> AI Quest Generations</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-system-purple">UNLIMITED</strong> AI Program Generations</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-system-purple">Priority 2x Faster</strong> AI Processing</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-system-purple">UNLIMITED</strong> Active Passive Skills</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-system-purple">LIFETIME</strong> History Retention</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-white">All 6 Themes</strong> + Custom Colors</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani">Data Export & API Access</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani"><strong className="text-white">1,000 💰</strong> Currency Bonus/month</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded bg-system-purple/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-system-purple text-[9px] sm:text-[10px]">✓</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-200 font-rajdhani">VIP Support (24h response)</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handleSubscribe('s_rank')}
                disabled={loading || currentTier === 's_rank'}
                className="w-full bg-gradient-to-b from-purple-800/60 via-purple-900/50 to-purple-950/60 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 text-white py-3 sm:py-4 font-orbitron font-bold text-[10px] sm:text-xs tracking-wider transition-all hover:scale-105 hover:shadow-neon-purple disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group touch-manipulation rounded-2xl"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                <span className="relative z-10">
                  {currentTier === 's_rank' ? '✓ CURRENT PLAN' : loading ? '⚙️ PROCESSING...' : '👑 UPGRADE TO S-RANK'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t-2 border-t-purple-500/30">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-[9px] sm:text-[10px] text-gray-400 font-rajdhani">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Secure payment powered by Stripe</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-gray-600"></div>
            <span>Cancel anytime, no questions asked</span>
            <div className="hidden sm:block w-px h-4 bg-gray-600"></div>
            <span>USD pricing • Monthly billing</span>
          </div>
        </div>
        </div>
      </div>

      {/* Support Message Modal */}
      {showSupportMessage && (
        <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-[9999] p-4 animate-fade-in">
          <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl shadow-purple-500/20 max-w-md w-full animate-scale-in"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">📧</div>
              <h3 className="text-xl font-bold text-white mb-4 font-orbitron">Contact Support</h3>
              <p className="text-gray-300 mb-6 font-rajdhani leading-relaxed">
                Due to issues with our payment provider, we're compensating affected users with a free one-month E-rank subscription.
                <br /><br />
                Please send a message via the support page with your in-game Name to receive your free E-rank subscription.
              </p>
              <button
                onClick={() => setShowSupportMessage(false)}
                className="bg-gradient-to-b from-purple-800/60 via-purple-900/50 to-purple-950/60 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 text-white px-6 py-3 text-sm font-bold tracking-wider rounded-2xl hover:scale-105 transition-all"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionModal;

